import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'
import { useLocationSearchQuery } from '@/api/accommodation/hooks'
import type { LocationSearchResult } from '@/api/accommodation/types'
import type { LocationSearchParams } from '@/features/accommodation/hooks/useLocatiomSearchParams'
import { ArticleOfferCard } from './ArticleOfferCard'

// Fetch a pool, dedupe by place, then show up to DEFAULT_MAX in a carousel
// (3 visible per view on desktop, scrollable to the rest).
const POOL_SIZE = 24
const DEFAULT_MAX = 12

interface ArticleOffersProps {
  /** Region slug, e.g. "sumava". Omit (or "all") for no region filter. */
  region?: string
  /** Label (category) id to filter by, e.g. "1" for Wellness & Relax. */
  label?: string
  beds?: string
  cribs?: string
  /** "true" → only adults-allowed stays. */
  adult?: string
  /** "true" → only pet-friendly stays. */
  pets?: string
  /** Max number of offers loaded into the carousel (default 12). */
  count?: string
  /**
   * Comma-separated facility keys to require (all must match), e.g.
   * "sauna" or "sauna,shower". Passed straight to the offer endpoint's
   * `facility` query param; the backend does the filtering.
   */
  facility?: string
  /**
   * Category slug to require (e.g. "glamping"). Resolved to the numeric
   * categoryId via enums inside useLocationSearchQuery.
   */
  category?: string
  /** CTA button label. */
  text?: string
}

function parseBool(value?: string): boolean {
  return value === 'true' || value === '1'
}

function parseIntOr<T>(value: string | undefined, fallback: T): number | T {
  if (value === undefined || value === '') return fallback
  const n = Number(value)
  return Number.isNaN(n) ? fallback : n
}

// Seeded shuffle so the random pick is stable across re-renders (the seed is
// generated once per mount), while still varying between visits.
function shuffleWithSeed<T>(items: T[], seed: number): T[] {
  const copy = [...items]
  let state = seed
  for (let i = copy.length - 1; i > 0; i--) {
    state = (state * 1103515245 + 12345) & 0x7fffffff
    const j = state % (i + 1)
    const current = copy[i]
    const other = copy[j]
    if (current === undefined || other === undefined) continue
    copy[i] = other
    copy[j] = current
  }
  return copy
}

/**
 * Shows current offers matching a homepage-style filter in a scrollable
 * carousel (3 per view on desktop), with a CTA to the identically-filtered
 * listing. Embedded in an article via e.g. `::nabidky{pets="true"}` or
 * `::nabidky{region="sumava" beds="4"}`.
 */
export function ArticleOffers(props: ArticleOffersProps) {
  const max = Math.max(1, parseIntOr(props.count, DEFAULT_MAX))
  const labelId = props.label ? parseIntOr(props.label, undefined) : undefined

  const filters: LocationSearchParams = {
    region: props.region ?? 'all',
    labelId: labelId === undefined ? undefined : labelId,
    beds: parseIntOr(props.beds, 1),
    cribs: parseIntOr(props.cribs, 0),
    adult: parseBool(props.adult),
    pets: parseBool(props.pets),
    // Trim whitespace, drop empties: "sauna, shower" → "sauna,shower".
    facility:
      props.facility
        ?.split(',')
        .map((key) => key.trim())
        .filter(Boolean)
        .join(',') || undefined,
    category: props.category,
    skip: 0,
    take: POOL_SIZE,
    order: 'start',
    direction: 'asc',
  }

  const { data, isPending } = useLocationSearchQuery(filters)

  const [seed] = useState(() => Math.floor(Math.random() * 0x7fffffff))
  const offers = useMemo(() => {
    if (!data) return []
    // One offer per place, then a random subset.
    const byLocation = new Map<string, LocationSearchResult>()
    for (const offer of data) {
      if (!byLocation.has(offer.location.slug)) {
        byLocation.set(offer.location.slug, offer)
      }
    }
    return shuffleWithSeed([...byLocation.values()], seed).slice(0, max)
  }, [data, seed, max])

  // CTA carries the same user-facing filter to the full listing.
  const ctaSearch = {
    region: filters.region,
    labelId: filters.labelId,
    beds: filters.beds,
    cribs: filters.cribs,
    adult: filters.adult,
    pets: filters.pets,
  }

  return (
    <div className="not-prose my-8">
      {isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="aspect-square animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : offers.length > 0 ? (
        <Carousel opts={{ align: 'start' }}>
          <CarouselContent>
            {offers.map((offer, i) => (
              <CarouselItem key={offer.location.slug} className="basis-4/5 sm:basis-1/2 lg:basis-1/3">
                <ArticleOfferCard offer={offer} position={i} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2 bg-white/90 shadow-md" />
          <CarouselNext className="right-2 bg-white/90 shadow-md" />
        </Carousel>
      ) : null}

      <div className="mt-6 flex justify-center">
        <Button asChild variant="red" size="lg">
          <Link to="/ubytovani/hledat" search={(prev) => ({ ...prev, ...ctaSearch })}>
            {props.text || 'Zobrazit ostatní nabídky'}
          </Link>
        </Button>
      </div>
    </div>
  )
}
